import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Browse All Product Categories');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="categories" className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3 font-heading">
            Browse Product Categories
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Select a category to explore structured product data sheets on the itaobuy spreadsheet platform
          </p>
        </div>

        {/* Dropdown */}
        <div className="flex justify-center mb-10">
          <div ref={dropdownRef} className="relative w-full max-w-md">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border border-background-200 rounded-lg text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className={selectedCategory === 'Browse All Product Categories' ? 'text-foreground-400' : 'text-foreground-700'}>
                {selectedCategory}
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-sm z-40 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleSelect(cat)}
                    className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors first:rounded-t-lg last:rounded-b-lg cursor-pointer whitespace-nowrap flex items-center gap-3"
                  >
                    <img src={cat.image} alt={cat.name} className="w-8 h-8 rounded object-cover flex-shrink-0" loading="lazy" />
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Category Buttons Grid with Images */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group flex flex-col rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-300 transition-all cursor-pointer hover:scale-[1.02]"
            >
              {/* Category Image */}
              <div className="w-full aspect-[4/3] overflow-hidden">
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              {/* Category Label */}
              <div className="px-3 py-2.5 flex items-center justify-center gap-1.5">
                <i className="ri-arrow-right-up-line text-xs text-foreground-400 group-hover:text-primary-500 transition-colors"></i>
                <span className="text-xs font-medium text-foreground-700 group-hover:text-primary-600 transition-colors text-center whitespace-nowrap">
                  {cat.name}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}